import React, { useState, useMemo, useCallback, Suspense, lazy } from "react";
import StepIndicator from "./StepIndicator";

// All lazy imports moved to top of file

import LoadingSpinner from "./LoadingSpinner";
import { StepSkeletonLoader } from "./SkeletonLoader";
import { sendToWebhook, generateSessionId } from "../utils/webhook";
import { validateStep as validateStepWithYup } from "../utils/validationSchemas";
import { showToast } from "./ToastNotification";

// Error Boundary for form steps
class FormErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Log error for debugging
    console.error("Form Error Boundary caught an error:", error, errorInfo);

    // You could also send this to an error reporting service
    // reportError(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Something went wrong with this step
                </h2>
                <p className="text-gray-600 mb-6">
                  We encountered an unexpected error. This is usually temporary.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Reload Page
                </button>
              </div>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                    Error Details (Development Only)
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-100 p-4 rounded overflow-auto max-h-40">
                    {this.state.error.toString()}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Professional Error Modal Component
const SubmissionErrorModal = ({ error, onRetry, onClose }) => {
  if (!error) return null;

  const handleRetry = () => {
    onRetry();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg
                className="w-5 h-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {error.type === "cors" ? "Connection Issue" : "Submission Error"}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-700 mb-3">{error.message}</p>
            {error.details && (
              <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
                {error.details}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleRetry}
              className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              Try Again
            </button>
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500 text-center">
            If this problem persists, please contact our support team.
          </p>
        </div>
      </div>
    </div>
  );
};

// Lazy load step components for better performance
const TimeManagementStep = lazy(() => import("./steps/TimeManagementStep"));
const GrowthOpportunityStep = lazy(() =>
  import("./steps/GrowthOpportunityStep")
);
const ClientValueStep = lazy(() => import("./steps/ClientValueStep"));
const MissedLeadCostStep = lazy(() => import("./steps/MissedLeadCostStep"));
const BusinessInfoStep = lazy(() => import("./steps/BusinessInfoStep"));
const LeadFlowStep = lazy(() => import("./steps/LeadFlowStep"));
const LeadResponseStep = lazy(() => import("./steps/LeadResponseStep"));
const TeamStructureStep = lazy(() => import("./steps/TeamStructureStep"));
const MissedOpportunitiesStep = lazy(() =>
  import("./steps/MissedOpportunitiesStep")
);
const LeadSourceFollowUpStep = lazy(() =>
  import("./steps/LeadSourceFollowUpStep")
);
const AutomationPreferencesStep = lazy(() =>
  import("./steps/AutomationPreferencesStep")
);
const StrategicInsightStep = lazy(() => import("./steps/StrategicInsightStep"));
const GoalsStep = lazy(() => import("./steps/GoalsStep"));
const PrimaryGoalStep = lazy(() => import("./steps/PrimaryGoalStep"));
const ContactInfoStep = lazy(() => import("./steps/ContactInfoStep"));

// Custom hook for form persistence
const useFormPersistence = (key) => {
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.warn("Failed to load saved form data:", error);
      return {};
    }
  });

  const updateData = useCallback(
    (newData) => {
      setData((prev) => {
        const updated =
          typeof newData === "function"
            ? newData(prev)
            : { ...prev, ...newData };
        try {
          localStorage.setItem(key, JSON.stringify(updated));
          // Show subtle save indicator (only occasionally to avoid spam)
          if (Math.random() < 0.05) {
            // 5% chance to show toast
            showToast("Progress saved automatically", "info", 2000);
          }
        } catch (error) {
          console.warn("Failed to save form data:", error);
          showToast("Unable to save progress locally", "warning", 3000);
        }
        return updated;
      });
    },
    [key]
  );

  const clearData = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setData({});
    } catch (error) {
      console.warn("Failed to clear form data:", error);
    }
  }, [key]);

  return [data, updateData, clearData];
};

const FormWizard = ({ onSubmit }) => {
  // Persist current step index
  const [persistedStep, setPersistedStep] = useState(() => {
    try {
      const saved = localStorage.getItem("ai-form-step");
      return saved ? parseInt(saved, 10) : 0;
    } catch (error) {
      return 0;
    }
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(persistedStep);
  const [sessionId] = useState(() => generateSessionId()); // Generate once and persist

  // Use persisted form data with fallback to default structure
  const [persistedData, updatePersistedData, clearPersistedData] =
    useFormPersistence("ai-form-data");

  const [formData, setFormData] = useState(() => ({
    // Session tracking
    sessionId: sessionId,
    // Growth Strategy & Time Management
    timeConsumingTasks: "",
    biggestGrowthChallenge: "",

    // Lead Handling & Response Analysis
    leadSources: [],
    leadResponse: "",
    lostClientDueToSlowResponse: "",
    missedLeadCost: "",
    leadsPerWeek: "",
    booksAppointments: "",
    noShowRate: "",
    afterHoursEnquiries: "",
    afterHoursShare: "",
    afterHoursRespond: "",

    // Team Structure & Coordination
    teamStructure: "",
    missedOpportunities: "",
    leadSourceFollowUp: "",
    tracksMissedCalls: "",

    // Revenue & Business Context
    extraClientValue: "",
    businessName: "",
    businessType: "",
    yearsInBusiness: "",
    employeeCount: "",
    currentRevenue: "",

    // Business Priorities & Goals
    automationPreferences: [],
    contentCreationMethod: "",
    currentTools: "",
    currentToolingShortcuts: [],
    primaryGoal: "",
    secondaryGoals: [],
    goals: [], // Keep for backward compatibility

    // Strategic & Contact Information
    pastClientSystem: "",
    customGrowthChallenge: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    additionalInfo: "",

    // Dynamic tracking
    completedSteps: [],
    userPath: [],

    // Merge with persisted data
    ...persistedData,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);

  // Static step configuration - components are lazy loaded
  const baseSteps = useMemo(
    () => [
      {
        id: "strategic-insight",
        title: "Growth Strategy",
        description: "Your biggest growth challenge?",
        component: StrategicInsightStep,
      },
      {
        id: "time-management",
        title: "Time Challenge",
        description: "What's eating your time?",
        component: TimeManagementStep,
      },
      {
        id: "lead-flow",
        title: "Lead Generation",
        description: "How do clients find you?",
        component: LeadFlowStep,
      },
      {
        id: "business-info",
        title: "Business Type",
        description: "What kind of business?",
        component: BusinessInfoStep,
      },
      {
        id: "lead-response",
        title: "Lead Response",
        description: "What happens when they reach out?",
        component: LeadResponseStep,
      },
      {
        id: "lost-client",
        title: "Lost Revenue",
        description: "Ever lost clients to slow response?",
        component: GrowthOpportunityStep,
      },
      {
        id: "missed-lead-cost",
        title: "Revenue Leak",
        description: "Cost of missed leads?",
        component: MissedLeadCostStep,
      },
      {
        id: "team-structure",
        title: "Team Structure",
        description: "Who handles follow-ups?",
        component: TeamStructureStep,
      },
      {
        id: "client-value",
        title: "Income Opportunity",
        description: "Value of one extra client?",
        component: ClientValueStep,
      },
      {
        id: "primary-goal",
        title: "Primary Goal",
        description: "Your #1 business priority?",
        component: PrimaryGoalStep,
      },
      {
        id: "secondary-goals",
        title: "Secondary Goals",
        description: "Other improvement areas?",
        component: GoalsStep,
      },
      {
        id: "automation-preferences",
        title: "Business Priorities",
        description: "What would you like to streamline?",
        component: AutomationPreferencesStep,
      },
      {
        id: "contact-info",
        title: "Contact Information",
        description: "Complete your profile",
        component: ContactInfoStep,
      },
    ],
    []
  ); // No dependencies - components are static

  // Dynamic steps based on form data - calculated separately
  const dynamicSteps = useMemo(() => {
    const additionalSteps = [];

    // Add missed opportunities step based on team structure
    if (
      formData.teamStructure === "solo" ||
      formData.teamStructure === "no-owner"
    ) {
      additionalSteps.push({
        id: "missed-opportunities-solo",
        title: "Missed Opportunities",
        description: "Do you miss calls or messages?",
        component: MissedOpportunitiesStep,
      });
    } else if (
      formData.teamStructure === "small-team" ||
      formData.teamStructure === "sales-team"
    ) {
      additionalSteps.push({
        id: "missed-opportunities-team",
        title: "Team Communication",
        description: "Team response challenges?",
        component: MissedOpportunitiesStep,
      });
    }

    // Add lead source follow-up step if they have qualifying sources
    const digitalSources = [
      "website-form",
      "facebook-ads",
      "google-ads",
      "instagram-dms",
      "whatsapp",
    ];
    const followupTriggerSources = [...digitalSources, "phone-calls"];

    if (
      formData.leadSources?.some((source) =>
        followupTriggerSources.includes(source)
      )
    ) {
      additionalSteps.push({
        id: "lead-source-followup",
        title: "Follow-up Process",
        description: "What happens after they reach out?",
        component: LeadSourceFollowUpStep,
      });
    }

    return additionalSteps;
  }, [formData.teamStructure, formData.leadSources]);

  // Combine static and dynamic steps - only recalculates when dynamic steps change
  const steps = useMemo(() => {
    const allSteps = [...baseSteps];

    // Insert dynamic steps at appropriate positions
    if (dynamicSteps.length > 0) {
      // Find insertion points for dynamic steps
      const teamStructureIndex = allSteps.findIndex(
        (step) => step.id === "team-structure"
      );
      const automationPrefsIndex = allSteps.findIndex(
        (step) => step.id === "automation-preferences"
      );

      // Add missed opportunities after team structure
      const missedOpportunitiesStep = dynamicSteps.find((step) =>
        step.id.includes("missed-opportunities")
      );
      if (missedOpportunitiesStep && teamStructureIndex !== -1) {
        allSteps.splice(teamStructureIndex + 1, 0, missedOpportunitiesStep);
      }

      // Add lead source follow-up before automation preferences
      const leadFollowupStep = dynamicSteps.find(
        (step) => step.id === "lead-source-followup"
      );
      if (leadFollowupStep && automationPrefsIndex !== -1) {
        allSteps.splice(automationPrefsIndex, 0, leadFollowupStep);
      }
    }

    return allSteps;
  }, [baseSteps, dynamicSteps]);

  const clearErrorsForValidFields = useCallback(
    (updatedData) => {
      const currentData = { ...formData, ...updatedData };
      const newErrors = { ...errors };
      let hasChanges = false;

      // Check each field that was updated
      Object.keys(updatedData).forEach((field) => {
        if (errors[field]) {
          let isValid = false;

          switch (field) {
            case "firstName":
            case "lastName":
              isValid = currentData[field] && currentData[field].trim() !== "";
              break;
            case "email":
              isValid =
                currentData[field] &&
                currentData[field].trim() !== "" &&
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                  currentData[field]
                );
              break;
            case "phone":
              isValid =
                currentData[field] &&
                currentData[field].trim() !== "" &&
                currentData[field].length >= 10;
              break;
            default:
              return;
          }

          if (isValid) {
            delete newErrors[field];
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setErrors(newErrors);
      }
    },
    [formData, errors]
  );

  const updateFormData = useCallback(
    (stepData) => {
      setFormData((prev) => {
        const updated = { ...prev, ...stepData };
        // Also update persisted data
        updatePersistedData(updated);
        return updated;
      });

      // Clear errors for fields that become valid (real-time validation)
      const currentStepId = steps[currentStepIndex]?.id;
      if (
        currentStepId === "contact-info" ||
        currentStepId === "business-info"
      ) {
        clearErrorsForValidFields(stepData);
      }
    },
    [updatePersistedData, steps, currentStepIndex, clearErrorsForValidFields]
  );

  const validateStep = useCallback(
    async (stepIndex) => {
      const step = steps[stepIndex];

      try {
        const validationResult = await validateStepWithYup(step.id, formData);
        setErrors(validationResult.errors);
        return validationResult.isValid;
      } catch (error) {
        console.error("Validation error:", error);
        setErrors({ general: "Validation failed. Please check your inputs." });
        return false;
      }
    },
    [steps, formData]
  );

  const handleNext = useCallback(async () => {
    const isValid = await validateStep(currentStepIndex);
    if (isValid) {
      if (currentStepIndex < steps.length - 1) {
        const nextStepIndex = currentStepIndex + 1;
        setCurrentStepIndex(nextStepIndex);
        setPersistedStep(nextStepIndex); // Persist step
        try {
          localStorage.setItem("ai-form-step", nextStepIndex.toString());
        } catch (error) {
          console.warn("Failed to save step progress:", error);
        }

        setFormData((prev) => ({
          ...prev,
          completedSteps: [...prev.completedSteps, steps[currentStepIndex].id],
        }));
        // Smooth scroll to top after React finishes rendering
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    }
  }, [currentStepIndex, steps, validateStep]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevStepIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevStepIndex);
      setPersistedStep(prevStepIndex); // Persist step
      try {
        localStorage.setItem("ai-form-step", prevStepIndex.toString());
      } catch (error) {
        console.warn("Failed to save step progress:", error);
      }
      setErrors({});
      // Smooth scroll to top after React finishes rendering
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [currentStepIndex]);

  const handleSubmit = useCallback(async () => {
    const isValid = await validateStep(currentStepIndex);
    if (isValid) {
      setIsLoading(true);
      try {
        const finalData = {
          ...formData,
          completedSteps: [
            ...formData.completedSteps,
            steps[currentStepIndex].id,
          ],
          userPath: steps.map((step) => step.id),
        };

        await sendToWebhook(finalData);

        // Show success toast
        showToast(
          "Your business assessment has been submitted successfully!",
          "success",
          5000
        );

        // Clear persisted data on successful submission
        clearPersistedData();
        try {
          localStorage.removeItem("ai-form-step");
        } catch (error) {
          console.warn("Failed to clear step progress:", error);
        }

        onSubmit(finalData);
      } catch (error) {
        console.error("Error submitting form:", error);

        // Professional error handling with modal
        if (
          error.message.includes("CORS") ||
          error.message.includes("NetworkError")
        ) {
          setSubmissionError({
            type: "cors",
            message:
              "We're experiencing a temporary connection issue. This is usually resolved quickly.",
            details:
              "Your information is safely saved and will be submitted once the connection is restored.",
          });
        } else {
          setSubmissionError({
            type: "general",
            message:
              "We encountered an issue while submitting your assessment. Please try again in a moment.",
            details:
              "If this continues, our team will be happy to assist you directly.",
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    currentStepIndex,
    formData,
    steps,
    clearPersistedData,
    onSubmit,
    validateStep,
  ]);

  // Memoized step props to prevent unnecessary re-renders
  const stepProps = useMemo(
    () => ({
      formData,
      updateFormData,
      errors,
      onNext: currentStepIndex === steps.length - 1 ? handleSubmit : handleNext,
      onPrev: currentStepIndex > 0 ? handlePrev : null,
    }),
    [
      formData,
      updateFormData,
      errors,
      currentStepIndex,
      steps.length,
      handleSubmit,
      handleNext,
      handlePrev,
    ]
  );

  // Memoized current step to prevent unnecessary recalculations
  const currentStep = useMemo(
    () => steps[currentStepIndex],
    [steps, currentStepIndex]
  );

  // Memoized step-specific props
  const stepSpecificProps = useMemo(() => {
    const props = {};

    if (currentStep?.id.includes("missed-opportunities")) {
      props.stepInfo = currentStep;
    }

    if (currentStep?.id === "contact-info") {
      props.onSubmit = handleSubmit;
      // Remove onNext for contact info step
      const { onNext, ...restProps } = stepProps;
      return { ...restProps, ...props };
    }

    return { ...stepProps, ...props };
  }, [currentStep, stepProps, handleSubmit]);

  const renderStep = useCallback(() => {
    if (!currentStep) return null;

    const StepComponent = currentStep.component;
    return <StepComponent {...stepSpecificProps} />;
  }, [currentStep, stepSpecificProps]);

  // Error modal handlers
  const handleErrorRetry = () => {
    setSubmissionError(null);
    // Retry the submission by calling handleSubmit again
    handleSubmit();
  };

  const handleErrorClose = () => {
    setSubmissionError(null);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <StepIndicator
              currentStep={currentStepIndex + 1}
              totalSteps={steps.length}
              currentPhase={steps[currentStepIndex]?.phase}
            />

            <div className="mt-8">
              <Suspense
                fallback={
                  <StepSkeletonLoader
                    stepId={steps[currentStepIndex]?.id || "default"}
                  />
                }
              >
                <FormErrorBoundary>{renderStep()}</FormErrorBoundary>
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Error Modal */}
      <SubmissionErrorModal
        error={submissionError}
        onRetry={handleErrorRetry}
        onClose={handleErrorClose}
      />
    </>
  );
};

export default FormWizard;
